import {Observer} from "mobx-react";
import type {CSSProperties} from "react";
import {useCallback, useLayoutEffect, useRef, useState} from "react";
import styled from "styled-components";
import {User} from "../../../data/models/User";
import {ReactUtils} from "../../utils/ReactUtils";
import type {Permission} from "../../../generated/api/base";
import {XyiconFeature} from "../../../generated/api/base";
import {UserGroup} from "../../../data/models/UserGroup";
import {DropdownSelectorStyled, SectionTitle, SharingPanelStyled} from "../sharing/SharingPanel.styled";
import {useAppStore} from "../../../StateManager";
import {useClickOutside} from "../utils";
import {SelectSearchFieldV5} from "../input/search/SelectSearchFieldV5";
import {GroupShareSelectorV5} from "../sharing/GroupShareSelectorV5";
import type {SelectedItem} from "../sharing/SharingUtils";
import {Functions} from "../../../utils/function/Functions";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {AppUtils} from "../../../utils/AppUtils";
import {TimeUtils} from "../../../utils/TimeUtils";
import {FocusLoss} from "../../../utils/ui/focus/FocusLoss";

interface IAddUserOrUserGroupProps {
	readonly ownedByUserId: string;
	readonly exceptions: string[];
	readonly selectedItemList: SelectedItem[];
	readonly setSelectedItems: (items: SelectedItem[]) => void;
	readonly onDeleteItem: (item: User | UserGroup) => void;
	readonly setPermissionType: (permission: Permission.View | Permission.Update) => void;
	readonly isGlobal?: boolean;
	readonly usersOnly?: boolean;
	readonly isDropdown?: boolean;
	readonly searchTextPlaceholder?: string;
	readonly onAdd?: (userId: string) => void;
	readonly showCurrentUserInTheSearchList?: boolean;
	readonly hiddenUserGroupIds?: string[];
	readonly disabled?: boolean;
	readonly hasEdit?: boolean;
}

export const AddUserOrUserGroupV5 = (props: IAddUserOrUserGroupProps) => {
	const appState = useAppStore((state) => state.appState);
	const {
		isGlobal,
		searchTextPlaceholder = "Enter user/group, comma separated",
		isDropdown = true,
		usersOnly = false,
		onAdd = Functions.emptyFunction,
		exceptions = [!isGlobal && appState.user?.id],
		hiddenUserGroupIds = [],
		ownedByUserId,
		selectedItemList,
		setSelectedItems,
		setPermissionType,
		onDeleteItem,
		disabled,
		hasEdit,
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [searchString, setSearchString] = useState<string>("");
	const [dropdownTransformation, setDropdownTransformation] = useState<CSSProperties>({
		zIndex: 8000,
	});

	const containerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const currentUser = appState.user;

	const onFocusLoss = () => {
		onToggleIsOpen(false);
	};

	const onToggleIsOpen = async (value?: boolean) => {
		if (!value && !isOpen) {
			setSearchString("");
			AppUtils.disableScrolling(false);
		}

		setIsOpen((o) => value ?? !o);

		if (!isOpen && value) {
			AppUtils.disableScrolling(true);

			await TimeUtils.wait(100);
			if (dropdownRef.current) {
				FocusLoss.listen(dropdownRef.current, onFocusLoss);
			}
		}
	};

	const onClose = useCallback(() => {
		setIsOpen(false);
		setSearchString("");
	}, []);

	useClickOutside([dropdownRef], onClose, "mousedown", []);

	const onItemSelect = (item: User | UserGroup) => {
		const {ownFeature: feature, id} = item;
		const itemName = feature === XyiconFeature.User ? item.firstName : item.name;
		const isItemSelected = selectedItemList.some((item) => item.id === id);

		if (!isItemSelected) {
			setSelectedItems([...selectedItemList, {id, itemName, feature}]);
		}

		onAdd(id);
		setSearchString("");
		AppUtils.disableScrolling(true);
	};

	const onItemRemove = (item: User | UserGroup) => {
		const updatedItems = selectedItemList.filter((selectedItem) => selectedItem.id !== item.id);
		setSelectedItems(updatedItems);
	};

	useLayoutEffect(() => {
		if (isOpen && containerRef.current && dropdownRef.current) {
			const listHeight = dropdownRef.current.offsetHeight;
			const parentSize = containerRef.current.getBoundingClientRect();
			const spaceBelow = window.innerHeight - parentSize.bottom;
			const lockedAlignment = spaceBelow >= listHeight ? VerticalAlignment.bottomOuter : VerticalAlignment.topOuter;

			const transform = DomUtils.getFixedFloatingElementPosition(
				containerRef.current,
				dropdownRef.current,
				lockedAlignment,
				HorizontalAlignment.left,
				0,
				0,
			).translate;
			const width = containerRef.current?.getBoundingClientRect().width;

			setDropdownTransformation((s) => ({...s, transform, width}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isOpen,
		selectedItemList.length, // To adjust the position when search field height changes (multi-line),
		searchString, // To adjust the position when dropdown height changes
		containerRef.current,
		dropdownRef.current,
	]);

	return (
		<Observer>
			{() => {
				let users = appState.actions.getList<User>(XyiconFeature.User);
				let userGroups = appState.actions
					.getList<UserGroup>(XyiconFeature.UserGroup)
					.filter((userGroup) => !hiddenUserGroupIds.includes(userGroup.id));
				let sharedUsers: User[] = [];
				let sharedUserGroups: UserGroup[] = [];

				if (!props.showCurrentUserInTheSearchList) {
					// Remove current user
					users = users.filter((user) => user.id !== currentUser.id);
				}

				// ...and the view's owner, but only if the view is NOT a global one.
				if (!isGlobal) {
					users = users.filter((user) => user.id !== ownedByUserId);
				}

				if (searchString) {
					users = users.filter((user) => User.search(user, searchString));
					userGroups = userGroups.filter((userGroup) => UserGroup.search(userGroup, searchString));
				}

				if (exceptions?.length) {
					sharedUsers = users.filter((user) => exceptions.includes(user.id));
					sharedUserGroups = userGroups.filter((userGroup) => exceptions.includes(userGroup.id));
				}

				return (
					<AddUserOrUserGroupStyled
						ref={containerRef}
						className={ReactUtils.cls({isOpen})}
					>
						<SelectSearchFieldV5
							value={searchString}
							onInput={setSearchString}
							onClick={() => onToggleIsOpen(true)}
							selectedItems={selectedItemList}
							placeholder={!isOpen && selectedItemList.length === 0 ? searchTextPlaceholder : ""}
							setPermissionType={setPermissionType}
							isDropdown={isDropdown}
							isSharing={true}
							removeSelectedItem={setSelectedItems}
							dropdownDisabled={disabled}
							hasEdit={hasEdit}
						/>
						{(isOpen || searchString) && (
							<DomPortal destination={DomPortal._body}>
								<DropdownSelectorStyled
									className="vbox"
									ref={dropdownRef}
									style={dropdownTransformation}
									$isDropdown={true}
									$fullWidth={true}
								>
									<SharingPanelStyled $isDropdown={true}>
										{!usersOnly && userGroups.length > 0 && (
											<div className="section borderBottom">
												<SectionTitle>User Groups</SectionTitle>
												<GroupShareSelectorV5
													items={userGroups}
													addedItems={sharedUserGroups}
													selectedItemList={selectedItemList}
													isSharing={true}
													isGlobal={isGlobal}
													onDeleteItem={onDeleteItem}
													onItemSelect={onItemSelect}
													onItemRemove={onItemRemove}
													feature={XyiconFeature.UserGroup}
												/>
											</div>
										)}
										{users.length > 0 && (
											<div className="section">
												<SectionTitle>Users</SectionTitle>
												<GroupShareSelectorV5
													items={users}
													addedItems={sharedUsers}
													selectedItemList={selectedItemList}
													isSharing={true}
													isGlobal={isGlobal}
													onDeleteItem={onDeleteItem}
													onItemSelect={onItemSelect}
													onItemRemove={onItemRemove}
													ownedByUserId={ownedByUserId}
													feature={XyiconFeature.User}
												/>
											</div>
										)}
										{userGroups.length === 0 && users.length === 0 && (
											<div className="empty">We couldn't find a match. To invite this user to Xyicon, please contact your Administrator.</div>
										)}
									</SharingPanelStyled>
								</DropdownSelectorStyled>
							</DomPortal>
						)}
					</AddUserOrUserGroupStyled>
				);
			}}
		</Observer>
	);
};

const AddUserOrUserGroupStyled = styled.div`
	flex: 1;
`;
